// src/components/tableros/TableroPrincipal.js
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Button,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  FormatSize as FormatSizeIcon,
  ColorLens as ColorLensIcon,
  Save as SaveIcon,
  Style as StyleIcon,
  EventNote as EventNoteIcon
} from '@mui/icons-material';
import { useEventContext } from '../../context/EventContext';
import { useDebounce } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import socketSyncService from '../../services/SocketSyncService';
import SpeedResultTable from './Tablas/SpeedResultTable';
import HorizontalEventTable from './Campo/Tablas/HorizontalEventTable';
import VerticalEventTable from './Campo/Tablas/VerticalEventTable';
import TabPanel from './common/TabPanel';
import ConnectionStatus from './common/ConnectionStatus';
import EventDetails from './common/EventDetails';
import GeneralTab from './tabs/GeneralTab';
import ColumnasTab from './tabs/ColumnasTab';
import TipografiaTab from './tabs/TipografiaTab';
import ColoresTab from './tabs/ColoresTab';
import TemasTab from './tabs/TemasTab';
import PresetsTab from './tabs/PresetsTab';
import EventosTab from './tabs/EventosTab';
import { themes } from './constants/themes';
import { defaultSettings } from './constants/defaultSettings';

export default function TableroPrincipal() {
  const {
    boardConfigs,
    saveBoardConfig,
    updateBoardConfig,
    deleteBoardConfig
  } = useEventContext();

  const [activeTab, setActiveTab] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [eventDetails, setEventDetails] = useState({
    eventName: 'No hay eventos seleccionados',
    event: '',
    round: '',
    heat: '',
    wind: '',
    participants: 0,
  });
  const [clock, setClock] = useState('0.0');
  const [results, setResults] = useState([]);

  const [showLane, setShowLane] = useState(false);
  const [showNumber, setShowNumber] = useState(false);
  const [showLastLapTime, setShowLastLapTime] = useState(false);
  const [showLapsToGo, setShowLapsToGo] = useState(false);
  const [showLapsColumns, setShowLapsColumns] = useState(false);
  const [showReactionTime, setShowReactionTime] = useState(false);
  const [showTimeDifference, setShowTimeDifference] = useState(false);
  const [showAttemptNumber, setShowAttemptNumber] = useState(true);
  const [displayRows, setDisplayRows] = useState(() => parseInt(localStorage.getItem('displayRows')) || 8);

  const [boardType, setBoardType] = useState('track');

  const [tableWidth, setTableWidth] = useState(1000);
  const [tableHeight, setTableHeight] = useState(1200);
  const [tableBackgroundColor, setTableBackgroundColor] = useState('#1a1a1a');
  const [globalFontColor, setGlobalFontColor] = useState('#ffffff');
  const [timerColor, setTimerColor] = useState('#00ff00');
  const [largeTimerColor, setLargeTimerColor] = useState('#00ff00');

  const [headerFontSize, setHeaderFontSize] = useState(24);
  const [nameFontSize, setNameFontSize] = useState(18);
  const [positionFontSize, setPositionFontSize] = useState(20);
  const [timeFontSize, setTimeFontSize] = useState(18);
  const [resultFontSize, setResultFontSize] = useState(54);
  const [attemptFontSize, setAttemptFontSize] = useState(18);
  const [teamFontSize, setTeamFontSize] = useState(24);
  const [lapTimeFontSize, setLapTimeFontSize] = useState(18);
  const [reactionTimeFontSize, setReactionTimeFontSize] = useState(16);
  const [differenceTimeFontSize, setDifferenceTimeFontSize] = useState(16);

  const [headerFontFamily, setHeaderFontFamily] = useState('Roboto');
  const [nameFontFamily, setNameFontFamily] = useState('Roboto');
  const [numbersFontFamily, setNumbersFontFamily] = useState('Roboto Mono');

  const [stopwatchSize, setStopwatchSize] = useState(50);
  const [largeStopwatchSize, setLargeStopwatchSize] = useState(120);

  const [headerFontColor, setHeaderFontColor] = useState('#ffffff');
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState('#2c0369');
  const [columnsHeaderColor, setColumnsHeaderColor] = useState('#4c516d');

  const [athleteNameColor, setAthleteNameColor] = useState('#ffffff');
  const [teamNameColor, setTeamNameColor] = useState('#e0e0e0');
  const [resultTimeColor, setResultTimeColor] = useState('#ffffff');
  const [deltaTimeColor, setDeltaTimeColor] = useState('#c1ff30');
  const [positionColor, setPositionColor] = useState('#ffffff');
  const [positionBackgroundColor, setPositionBackgroundColor] = useState('#4c516d');
  const [nameBackgroundColor, setNameBackgroundColor] = useState('#515e7b');
  const [tableRowOddColor, setTableRowOddColor] = useState('#252525');
  const [tableRowEvenColor, setTableRowEvenColor] = useState('#1e1e1e');

  const [laneColor, setLaneColor] = useState('#ffffff');
  const [numberColor, setNumberColor] = useState('#ffffff');

  const [markBackgroundColor, setMarkBackgroundColor] = useState('#000000');
  const [attemptColor, setAttemptColor] = useState('#ffffff');
  const [attemptBackgroundColor, setAttemptBackgroundColor] = useState('#0d47a1');
  const [lastLapTimeColor, setLastLapTimeColor] = useState('#ffffff');
  const [reactionTimeColor, setReactionTimeColor] = useState('#ffffff');
  const [lapsToGoColor, setLapsToGoColor] = useState('#ffffff');
  const [lapsColumnsColor, setLapsColumnsColor] = useState('#ffffff');

  const [successColor, setSuccessColor] = useState('#1b5e20');
  const [failColor, setFailColor] = useState('#b71c1c');
  const [passColor, setPassColor] = useState('#0d47a1');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [currentConfig, setCurrentConfig] = useState({});
  const [debouncedConfig] = useDebounce(currentConfig, 500);

  const [selectedConfig, setSelectedConfig] = useState('');
  const [configName, setConfigName] = useState('');
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const allConfigs = {
    general: {
      tableWidth,
      tableHeight,
      tableBackgroundColor,
      globalFontColor,
      displayRows,
      boardType
    },
    typography: {
      headerFontSize,
      headerFontFamily,
      nameFontSize,
      nameFontFamily,
      positionFontSize,
      timeFontSize,
      teamFontSize,
      numbersFontFamily,
      resultFontSize,
      attemptFontSize,
      lapTimeFontSize,
      reactionTimeFontSize,
      differenceTimeFontSize
    },
    colors: {
      timerColor,
      largeTimerColor,
      headerBackgroundColor,
      headerFontColor,
      columnsHeaderColor,
      athleteNameColor,
      teamNameColor,
      resultTimeColor,
      deltaTimeColor,
      positionColor,
      positionBackgroundColor,
      nameBackgroundColor,
      laneColor,
      numberColor,
      tableRowOddColor,
      tableRowEvenColor,
      markBackgroundColor,
      attemptColor,
      attemptBackgroundColor,
      lastLapTimeColor,
      reactionTimeColor,
      lapsToGoColor,
      lapsColumnsColor,
      successColor,
      failColor,
      passColor
    },
    columns: {
      showLane,
      showNumber,
      showLastLapTime,
      showLapsToGo,
      showLapsColumns,
      showReactionTime,
      showTimeDifference,
      showAttemptNumber
    },
    stopwatch: {
      stopwatchSize,
      largeStopwatchSize
    }
  };

  const getTrackProps = () => ({
    results,
    setResults,
    eventName: eventDetails,
    clock,
    showLane: allConfigs.columns.showLane,
    showNumber: allConfigs.columns.showNumber,
    showLastLapTime: allConfigs.columns.showLastLapTime,
    showLapsToGo: allConfigs.columns.showLapsToGo,
    showLapsColumns: allConfigs.columns.showLapsColumns,
    displayRows: allConfigs.general.displayRows,
    showTimeDifference: allConfigs.columns.showTimeDifference,
    showReactionTime: allConfigs.columns.showReactionTime,
    tableWidth: allConfigs.general.tableWidth,
    tableHeight: allConfigs.general.tableHeight,
    tableBackgroundColor: allConfigs.general.tableBackgroundColor,
    globalFontColor: allConfigs.general.globalFontColor,
    timerColor: allConfigs.colors.timerColor,
    headerFontSize: allConfigs.typography.headerFontSize,
    nameFontSize: allConfigs.typography.nameFontSize,
    positionFontSize: allConfigs.typography.positionFontSize,
    timeFontSize: allConfigs.typography.timeFontSize,
    stopwatchSize: allConfigs.stopwatch.stopwatchSize,
    largeStopwatchSize: allConfigs.stopwatch.largeStopwatchSize,
    headerFontColor: allConfigs.colors.headerFontColor,
    athleteNameColor: allConfigs.colors.athleteNameColor,
    teamNameColor: allConfigs.colors.teamNameColor,
    resultTimeColor: allConfigs.colors.resultTimeColor,
    deltaTimeColor: allConfigs.colors.deltaTimeColor,
    positionColor: allConfigs.colors.positionColor,
    laneColor: allConfigs.colors.laneColor,
    numberColor: allConfigs.colors.numberColor,
    lastLapTimeColor: allConfigs.colors.lastLapTimeColor,
    reactionTimeColor: allConfigs.colors.reactionTimeColor,
    lapsToGoColor: allConfigs.colors.lapsToGoColor,
    lapsColumnsColor: allConfigs.colors.lapsColumnsColor,
    tableRowOddColor: allConfigs.colors.tableRowOddColor,
    tableRowEvenColor: allConfigs.colors.tableRowEvenColor,
    boardType: allConfigs.general.boardType,
    headerFontFamily: allConfigs.typography.headerFontFamily,
    nameFontFamily: allConfigs.typography.nameFontFamily,
    numbersFontFamily: allConfigs.typography.numbersFontFamily,
  });

  const getHorizontalFieldProps = () => ({
    eventName: eventDetails.eventName,
    eventSubtitle: `${eventDetails.event ? `Evento: ${eventDetails.event}` : ''} ${eventDetails.round ? `• Ronda: ${eventDetails.round}` : ''} ${eventDetails.heat ? `• Heat: ${eventDetails.heat}` : ''}`,
    displayRows: allConfigs.general.displayRows,
    tableWidth: allConfigs.general.tableWidth,
    tableHeight: allConfigs.general.tableHeight,
    tableBackgroundColor: allConfigs.general.tableBackgroundColor,
    headerColor: allConfigs.colors.headerBackgroundColor,
    positionColor: allConfigs.colors.positionColor,
    positionBackgroundColor: allConfigs.colors.positionBackgroundColor,
    nameBackgroundColor: allConfigs.colors.nameBackgroundColor,
    markBackgroundColor: allConfigs.colors.markBackgroundColor,
    attemptBackgroundColor: allConfigs.colors.attemptBackgroundColor,
    headerFontColor: allConfigs.colors.headerFontColor,
    athleteNameColor: allConfigs.colors.athleteNameColor,
    teamNameColor: allConfigs.colors.teamNameColor,
    resultColor: allConfigs.colors.resultTimeColor,
    attemptColor: allConfigs.colors.attemptColor,
    laneColor: allConfigs.colors.laneColor,
    tableRowOddColor: allConfigs.colors.tableRowOddColor,
    tableRowEvenColor: allConfigs.colors.tableRowEvenColor,
    headerFontSize: allConfigs.typography.headerFontSize,
    positionFontSize: allConfigs.typography.positionFontSize,
    nameFontSize: allConfigs.typography.nameFontSize,
    teamFontSize: allConfigs.typography.teamFontSize,
    resultFontSize: allConfigs.typography.resultFontSize,
    attemptFontSize: allConfigs.typography.attemptFontSize,
    showLane: allConfigs.columns.showLane,
    showAttemptNumber: allConfigs.columns.showAttemptNumber,
    headerFontFamily: allConfigs.typography.headerFontFamily,
    nameFontFamily: allConfigs.typography.nameFontFamily,
    numbersFontFamily: allConfigs.typography.numbersFontFamily,
  });

  const getVerticalFieldProps = () => ({
    eventName: eventDetails.eventName,
    eventSubtitle: `${eventDetails.event ? `Evento: ${eventDetails.event}` : ''} ${eventDetails.round ? `• Ronda: ${eventDetails.round}` : ''} ${eventDetails.heat ? `• Heat: ${eventDetails.heat}` : ''}`,
    displayRows: allConfigs.general.displayRows,
    tableWidth: allConfigs.general.tableWidth,
    tableHeight: allConfigs.general.tableHeight,
    tableBackgroundColor: allConfigs.general.tableBackgroundColor,
    headerColor: allConfigs.colors.headerBackgroundColor,
    positionColor: allConfigs.colors.positionColor,
    positionBackgroundColor: allConfigs.colors.positionBackgroundColor,
    nameBackgroundColor: allConfigs.colors.nameBackgroundColor,
    heightBackgroundColor: allConfigs.colors.markBackgroundColor,
    headerFontColor: allConfigs.colors.headerFontColor,
    athleteNameColor: allConfigs.colors.athleteNameColor,
    teamNameColor: allConfigs.colors.teamNameColor,
    heightColor: allConfigs.colors.resultTimeColor,
    failColor: allConfigs.colors.failColor,
    passColor: allConfigs.colors.passColor,
    successColor: allConfigs.colors.successColor,
    laneColor: allConfigs.colors.laneColor,
    tableRowOddColor: allConfigs.colors.tableRowOddColor,
    tableRowEvenColor: allConfigs.colors.tableRowEvenColor,
    headerFontSize: allConfigs.typography.headerFontSize,
    positionFontSize: allConfigs.typography.positionFontSize,
    nameFontSize: allConfigs.typography.nameFontSize,
    teamFontSize: allConfigs.typography.teamFontSize,
    heightFontSize: allConfigs.typography.resultFontSize,
    attemptFontSize: allConfigs.typography.attemptFontSize,
    showLane: allConfigs.columns.showLane,
    headerFontFamily: allConfigs.typography.headerFontFamily,
    nameFontFamily: allConfigs.typography.nameFontFamily,
    numbersFontFamily: allConfigs.typography.numbersFontFamily,
  });

  const formatTime = (timeString) => timeString.split('.')[0];

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const applyTheme = (themeName) => {
    const theme = themes[themeName];
    if (!theme) return;
    setTableBackgroundColor(theme.tableBackgroundColor);
    setGlobalFontColor(theme.globalFontColor);
    setHeaderBackgroundColor(theme.headerBackgroundColor);
    setHeaderFontColor(theme.headerFontColor);
    setColumnsHeaderColor(theme.columnsHeaderColor);
    setPositionColor(theme.positionColor);
    setPositionBackgroundColor(theme.positionBackgroundColor);
    setNameBackgroundColor(theme.nameBackgroundColor);
    setAthleteNameColor(theme.athleteNameColor);
    setTeamNameColor(theme.teamNameColor);
    setResultTimeColor(theme.resultTimeColor);
    setDeltaTimeColor(theme.deltaTimeColor);
    setTimerColor(theme.timerColor);
    setLargeTimerColor(theme.largeTimerColor || theme.timerColor);
    setTableRowOddColor(theme.tableRowOddColor);
    setTableRowEvenColor(theme.tableRowEvenColor);
    setLaneColor(theme.laneColor);
    setNumberColor(theme.numberColor);
    setMarkBackgroundColor(theme.markBackgroundColor);
    setAttemptColor(theme.attemptColor);
    setAttemptBackgroundColor(theme.attemptBackgroundColor);
    setLastLapTimeColor(theme.lastLapTimeColor);
    setReactionTimeColor(theme.reactionTimeColor);
    setLapsToGoColor(theme.lapsToGoColor);
    setLapsColumnsColor(theme.lapsColumnsColor);
    if (theme.failColor) setFailColor(theme.failColor);
    if (theme.passColor) setPassColor(theme.passColor);
    if (theme.successColor) setSuccessColor(theme.successColor);
    showSnackbar(`Tema "${themeName}" aplicado`, 'success');
  };

  const resetToDefaultColors = () => {
    setTableBackgroundColor(defaultSettings.tableBackgroundColor);
    setGlobalFontColor(defaultSettings.globalFontColor);
    setTimerColor(defaultSettings.timerColor);
    setLargeTimerColor(defaultSettings.largeTimerColor);
    setHeaderBackgroundColor(defaultSettings.headerBackgroundColor);
    setHeaderFontColor(defaultSettings.headerFontColor);
    setColumnsHeaderColor(defaultSettings.columnsHeaderColor);
    setAthleteNameColor(defaultSettings.athleteNameColor);
    setTeamNameColor(defaultSettings.teamNameColor);
    setResultTimeColor(defaultSettings.resultTimeColor);
    setDeltaTimeColor(defaultSettings.deltaTimeColor);
    setPositionColor(defaultSettings.positionColor);
    setPositionBackgroundColor(defaultSettings.positionBackgroundColor);
    setNameBackgroundColor(defaultSettings.nameBackgroundColor);
    setLaneColor(defaultSettings.laneColor);
    setNumberColor(defaultSettings.numberColor);
    setMarkBackgroundColor(defaultSettings.markBackgroundColor);
    setAttemptColor(defaultSettings.attemptColor);
    setAttemptBackgroundColor(defaultSettings.attemptBackgroundColor);
    setLastLapTimeColor(defaultSettings.lastLapTimeColor);
    setReactionTimeColor(defaultSettings.reactionTimeColor);
    setLapsToGoColor(defaultSettings.lapsToGoColor);
    setLapsColumnsColor(defaultSettings.lapsColumnsColor);
    setTableRowOddColor(defaultSettings.tableRowOddColor);
    setTableRowEvenColor(defaultSettings.tableRowEvenColor);
    setSuccessColor(defaultSettings.successColor);
    setFailColor(defaultSettings.failColor);
    setPassColor(defaultSettings.passColor);
    showSnackbar('Colores restaurados a los predeterminados', 'success');
  };

  const resetToDefault = () => {
    setTableWidth(defaultSettings.tableWidth);
    setTableHeight(defaultSettings.tableHeight);
    setDisplayRows(defaultSettings.displayRows);
    setStopwatchSize(defaultSettings.stopwatchSize);
    setLargeStopwatchSize(defaultSettings.largeStopwatchSize);
    showSnackbar('Valores generales restaurados a los predeterminados', 'success');
  };

  const openCurrentTablero = () => {
    const configId = 'currentConfig';
    const currentConfigToSave = {
      id: configId,
      name: eventDetails.eventName !== 'No hay eventos seleccionados'
        ? eventDetails.eventName
        : 'Configuración Actual',
      eventData: eventDetails,
      boardType,
      settings: {
        ...allConfigs.general,
        ...allConfigs.typography,
        ...allConfigs.colors,
        ...allConfigs.columns,
        ...allConfigs.stopwatch,
      }
    };
    localStorage.setItem(`config-${configId}`, JSON.stringify(currentConfigToSave));
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const width = Math.min(tableWidth + 40, screenWidth);
    const height = Math.min(tableHeight + 40, screenHeight);
    const newWindow = window.open(
      `/scoreboard?id=${configId}&type=${boardType}`,
      `tablero-${configId}`,
      `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`
    );
    if (!newWindow) alert('Permite ventanas emergentes para abrir el tablero');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    switch (name) {
      case 'showLane': return setShowLane(checked);
      case 'showNumber': return setShowNumber(checked);
      case 'showLastLapTime': return setShowLastLapTime(checked);
      case 'showLapsToGo': return setShowLapsToGo(checked);
      case 'showLapsColumns': return setShowLapsColumns(checked);
      case 'showTimeDifference': return setShowTimeDifference(checked);
      case 'showReactionTime': return setShowReactionTime(checked);
      case 'showAttemptNumber': return setShowAttemptNumber(checked);
      default: return;
    }
  };

  const handleDisplayRowsChange = (e) => {
    const value = Math.max(1, parseInt(e.target.value) || 1);
    setDisplayRows(value);
    localStorage.setItem('displayRows', value);
    showSnackbar('Número de filas visibles actualizado', 'success');
  };

  const handleBoardTypeChange = (type) => {
    setBoardType(type);
    localStorage.setItem('boardType', type);
    showSnackbar(`Tipo de tablero cambiado a ${type}`, 'info');
  };

  const saveConfiguration = async () => {
    if (!configName.trim()) {
      showSnackbar('El nombre de la configuración no puede estar vacío', 'error');
      return;
    }
    const newConfig = {
      id: uuidv4(),
      name: configName,
      boardType,
      settings: {
        ...allConfigs.general,
        ...allConfigs.typography,
        ...allConfigs.colors,
        ...allConfigs.columns,
        ...allConfigs.stopwatch,
      }
    };
    await saveBoardConfig(newConfig);
    showSnackbar('Configuración guardada', 'success');
    setIsSaveDialogOpen(false);
    setConfigName('');
  };

  const loadConfiguration = (configId) => {
    const cfg = boardConfigs.find(c => c.id === configId);
    if (!cfg) return;
    const s = cfg.settings;
    setBoardType(cfg.boardType);
    setDisplayRows(s.displayRows);
    setTableWidth(s.tableWidth);
    setTableHeight(s.tableHeight);
    setStopwatchSize(s.stopwatchSize);
    setLargeStopwatchSize(s.largeStopwatchSize);
    setHeaderFontSize(s.headerFontSize);
    setNameFontSize(s.nameFontSize);
    setPositionFontSize(s.positionFontSize);
    setTimeFontSize(s.timeFontSize);
    setResultFontSize(s.resultFontSize);
    setAttemptFontSize(s.attemptFontSize);
    setTeamFontSize(s.teamFontSize);
    setLapTimeFontSize(s.lapTimeFontSize);
    setReactionTimeFontSize(s.reactionTimeFontSize);
    setDifferenceTimeFontSize(s.differenceTimeFontSize);
    setHeaderFontFamily(s.headerFontFamily);
    setNameFontFamily(s.nameFontFamily);
    setNumbersFontFamily(s.numbersFontFamily);
    setTableBackgroundColor(s.tableBackgroundColor);
    setGlobalFontColor(s.globalFontColor);
    setTimerColor(s.timerColor);
    setLargeTimerColor(s.largeTimerColor);
    setHeaderBackgroundColor(s.headerBackgroundColor);
    setHeaderFontColor(s.headerFontColor);
    setColumnsHeaderColor(s.columnsHeaderColor);
    setAthleteNameColor(s.athleteNameColor);
    setTeamNameColor(s.teamNameColor);
    setResultTimeColor(s.resultTimeColor);
    setDeltaTimeColor(s.deltaTimeColor);
    setPositionColor(s.positionColor);
    setPositionBackgroundColor(s.positionBackgroundColor);
    setNameBackgroundColor(s.nameBackgroundColor);
    setLaneColor(s.laneColor);
    setNumberColor(s.numberColor);
    setMarkBackgroundColor(s.markBackgroundColor);
    setAttemptColor(s.attemptColor);
    setAttemptBackgroundColor(s.attemptBackgroundColor);
    setLastLapTimeColor(s.lastLapTimeColor);
    setReactionTimeColor(s.reactionTimeColor);
    setLapsToGoColor(s.lapsToGoColor);
    setLapsColumnsColor(s.lapsColumnsColor);
    setTableRowOddColor(s.tableRowOddColor);
    setTableRowEvenColor(s.tableRowEvenColor);
    setSuccessColor(s.successColor);
    setFailColor(s.failColor);
    setPassColor(s.passColor);
    setShowLane(s.showLane);
    setShowNumber(s.showNumber);
    setShowLastLapTime(s.showLastLapTime);
    setShowLapsToGo(s.showLapsToGo);
    setShowLapsColumns(s.showLapsColumns);
    setShowReactionTime(s.showReactionTime);
    setShowTimeDifference(s.showTimeDifference);
    setShowAttemptNumber(s.showAttemptNumber);
    showSnackbar(`Configuración "${cfg.name}" cargada`, 'success');
  };

  useEffect(() => {
    const handleMessage = (message) => {
      try {
        const obj = JSON.parse(message);
        if (obj[1] && obj[1].length >= 6) {
          const nd = {
            eventName: obj[1][1] || 'No hay eventos seleccionados',
            wind: obj[1][2] || 'NM',
            event: obj[1][3] || '0',
            round: obj[1][4] || '0',
            heat: obj[1][5] || '0',
            participants: obj[1][8] || '0',
          };
          setEventDetails(nd);
          if (nd.eventName === 'No hay eventos seleccionados') {
            setResults([]);
            setClock('0.0');
          }
        }
        if (obj[0]) setClock(formatTime(obj[0][0]));
        if (obj.length > 2) setResults(obj.slice(2));
      } catch {}
    };
    const unsubscribeMessage = socketSyncService.on('message', handleMessage);
    const unsubscribeClear = socketSyncService.on('clearBoard', () => {
      setResults([]); setClock('0.0');
    });
    return () => {
      unsubscribeMessage();
      unsubscribeClear();
    };
  }, []);

  useEffect(() => {
    const updateStatus = () => setIsConnected(socketSyncService.getConnectionStatus().isConnected);
    const unsubConn = socketSyncService.on('connect', () => showSnackbar('Conectado al servidor de eventos','success'));
    const unsubDisc = socketSyncService.on('disconnect', () => showSnackbar('Desconectado del servidor de eventos','warning'));
    updateStatus();
    const iv = setInterval(updateStatus, 5000);
    return () => {
      unsubConn(); unsubDisc(); clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    setCurrentConfig({
      boardType,
      settings: {
        ...allConfigs.general,
        ...allConfigs.typography,
        ...allConfigs.colors,
        ...allConfigs.columns,
        ...allConfigs.stopwatch,
      }
    });
  }, [
    boardType, displayRows, tableWidth, tableHeight, tableBackgroundColor, globalFontColor,
    headerFontSize, nameFontSize, positionFontSize, timeFontSize, headerFontFamily, nameFontFamily, numbersFontFamily,
    resultFontSize, attemptFontSize, teamFontSize, lapTimeFontSize, reactionTimeFontSize, differenceTimeFontSize,
    timerColor, largeTimerColor, headerBackgroundColor, headerFontColor, columnsHeaderColor,
    athleteNameColor, teamNameColor, resultTimeColor, deltaTimeColor, positionColor,
    positionBackgroundColor, nameBackgroundColor, laneColor, numberColor, tableRowOddColor, tableRowEvenColor,
    markBackgroundColor, attemptColor, attemptBackgroundColor, lastLapTimeColor, reactionTimeColor,
    lapsToGoColor, lapsColumnsColor, successColor, failColor, passColor,
    showLane, showNumber, showLastLapTime, showLapsToGo, showLapsColumns, showReactionTime,
    showTimeDifference, showAttemptNumber, stopwatchSize, largeStopwatchSize
  ]);

  useEffect(() => {
    if (debouncedConfig.settings) {
      localStorage.setItem('currentConfiguration', JSON.stringify(debouncedConfig));
    }
  }, [debouncedConfig]);

  const tabProps = {
    tableWidth, setTableWidth,
    tableHeight, setTableHeight,
    tableBackgroundColor, setTableBackgroundColor,
    globalFontColor, setGlobalFontColor,
    timerColor, setTimerColor,
    largeTimerColor, setLargeTimerColor,
    headerFontSize, setHeaderFontSize,
    nameFontSize, setNameFontSize,
    positionFontSize, setPositionFontSize,
    timeFontSize, setTimeFontSize,
    resultFontSize, setResultFontSize,
    attemptFontSize, setAttemptFontSize,
    teamFontSize, setTeamFontSize,
    lapTimeFontSize, setLapTimeFontSize,
    reactionTimeFontSize, setReactionTimeFontSize,
    differenceTimeFontSize, setDifferenceTimeFontSize,
    headerFontFamily, setHeaderFontFamily,
    nameFontFamily, setNameFontFamily,
    numbersFontFamily, setNumbersFontFamily,
    stopwatchSize, setStopwatchSize,
    largeStopwatchSize, setLargeStopwatchSize,
    headerFontColor, setHeaderFontColor,
    headerBackgroundColor, setHeaderBackgroundColor,
    columnsHeaderColor, setColumnsHeaderColor,
    athleteNameColor, setAthleteNameColor,
    teamNameColor, setTeamNameColor,
    resultTimeColor, setResultTimeColor,
    deltaTimeColor, setDeltaTimeColor,
    positionColor, setPositionColor,
    positionBackgroundColor, setPositionBackgroundColor,
    nameBackgroundColor, setNameBackgroundColor,
    laneColor, setLaneColor,
    numberColor, setNumberColor,
    markBackgroundColor, setMarkBackgroundColor,
    attemptColor, setAttemptColor,
    attemptBackgroundColor, setAttemptBackgroundColor,
    lastLapTimeColor, setLastLapTimeColor,
    reactionTimeColor, setReactionTimeColor,
    lapsToGoColor, setLapsToGoColor,
    lapsColumnsColor, setLapsColumnsColor,
    tableRowOddColor, setTableRowOddColor,
    tableRowEvenColor, setTableRowEvenColor,
    successColor, setSuccessColor,
    failColor, setFailColor,
    passColor, setPassColor,
    showLane, setShowLane,
    showNumber, setShowNumber,
    showLastLapTime, setShowLastLapTime,
    showLapsToGo, setShowLapsToGo,
    showLapsColumns, setShowLapsColumns,
    showReactionTime, setShowReactionTime,
    showTimeDifference, setShowTimeDifference,
    showAttemptNumber, setShowAttemptNumber,
    displayRows, setDisplayRows,
    handleCheckboxChange,
    handleDisplayRowsChange,
    showSnackbar,
    resetToDefault,
    resetToDefaultColors,
    openCurrentTablero,
    isMobile,
    boardType,
    setBoardType: handleBoardTypeChange,
  };

  const temasProps = {
    themes,
    applyTheme,
    tableBackgroundColor,
    globalFontColor,
    timerColor,
    headerFontColor,
    athleteNameColor,
    teamNameColor,
    resultTimeColor,
    deltaTimeColor,
    positionColor,
    tableRowOddColor,
    tableRowEvenColor,
    showSnackbar,
    isMobile,
  };

  const presetsProps = {
    configurations: boardConfigs,
    selectedConfig,
    setSelectedConfig,
    configName,
    setConfigName,
    loadConfiguration,
    saveConfiguration,
    isMobile,
  };

  const eventosProps = {
    boardType,
    setBoardType: handleBoardTypeChange,
    openCurrentTablero,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ConnectionStatus 
            isConnected={isConnected} 
            reconnect={() => {
              if (!isConnected) {
                const result = socketSyncService.reconnect();
                showSnackbar(result
                  ? 'Intentando reconectar...'
                  : 'Solicitud de reconexión enviada a la ventana principal'
                , 'info');
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <EventDetails 
            eventDetails={eventDetails} 
            clock={clock} 
            timerColor={timerColor}
            isMobile={isMobile}
          />
        </Grid>
        <Grid item xs={12}>
          <Card elevation={3} sx={{ borderRadius: 2, overflow: 'visible' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={(e,v)=>setActiveTab(v)}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons={isMobile ? "auto" : false}
                allowScrollButtonsMobile
                aria-label="configuración de tablero"
              >
                <Tab icon={<EventNoteIcon />} label="Eventos" />
                <Tab icon={<SettingsIcon />} label="General" />
                <Tab icon={<VisibilityIcon />} label="Columnas" />
                <Tab icon={<FormatSizeIcon />} label="Tipografía" />
                <Tab icon={<ColorLensIcon />} label="Colores" />
                <Tab icon={<SaveIcon />} label="Presets" />
                <Tab icon={<StyleIcon />} label="Temas" />
              </Tabs>
            </Box>
            <TabPanel value={activeTab} index={0}>
              <EventosTab {...eventosProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <GeneralTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <ColumnasTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
              <TipografiaTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={4}>
              <ColoresTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={5}>
              <PresetsTab {...presetsProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={6}>
              <TemasTab {...temasProps} />
            </TabPanel>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, overflow: 'hidden', mt: 3 }}>
            <CardHeader 
              title={`Previsualización: Tablero de ${
                boardType === 'track' ? 'Pista' :
                boardType === 'horizontalField' ? 'Campo Horizontal' :
                'Campo Vertical'
              }`}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}
              action={
                <Button
                  startIcon={<OpenInNewIcon />}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={openCurrentTablero}
                >
                  Abrir en Nueva Ventana
                </Button>
              }
            />
            <CardContent sx={{ p: 0, maxHeight: 600, overflow: 'auto' }}>
              {boardType === 'track' && <SpeedResultTable {...getTrackProps()} />}
              {boardType === 'horizontalField' && <HorizontalEventTable {...getHorizontalFieldProps()} />}
              {boardType === 'verticalField' && <VerticalEventTable {...getVerticalFieldProps()} />}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={()=>setSnackbarOpen(false)}>
        <Alert onClose={()=>setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
